import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@command-center/ui-system';
import { Link2, NotebookPen } from 'lucide-react';
import { RecordNotes } from '@command-center/oneup-portal-ui';
import type { SharedRecordNote } from '@oneup/shared';
import { leadloopApi, type ActivityItem } from '../lib/leadloop-api';
import { DetailSection } from '@command-center/ui-system';
import { formatDateTime } from '../views/parts';

export function RelatedRecordList({
  items,
  empty,
}: {
  /** `to` is optional: some related records have no detail route of their own. */
  items: Array<{ id: string; to?: string; title: string; subtitle?: string; meta?: string }>;
  empty: string;
}) {
  if (items.length === 0) return <p className="text-sm text-muted-foreground">{empty}</p>;
  return (
    <div className="divide-y divide-border overflow-hidden rounded-md border border-border">
      {items.map((item) => {
        const body = (
          <>
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
              <Link2 className="h-3.5 w-3.5" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium text-foreground">{item.title}</span>
              {item.subtitle && <span className="block truncate text-xs text-muted-foreground">{item.subtitle}</span>}
            </span>
            {item.meta && <span className="shrink-0 text-xs text-muted-foreground">{item.meta}</span>}
          </>
        );
        return item.to ? (
          <Link key={item.id} to={item.to} className="flex items-center gap-3 px-3 py-2.5 hover:bg-muted/60">
            {body}
          </Link>
        ) : (
          <div key={item.id} className="flex items-center gap-3 px-3 py-2.5">{body}</div>
        );
      })}
    </div>
  );
}

export function ActivityTimeline({ items, empty = 'No activity recorded yet.' }: { items: ActivityItem[]; empty?: string }) {
  if (items.length === 0) return <p className="text-sm text-muted-foreground">{empty}</p>;
  return (
    <ol className="relative ml-1 border-l border-border pl-4">
      {items.map((item, index) => {
        const description = typeof item.metadata?.description === 'string' ? item.metadata.description : undefined;
        return (
          <li key={item.id ?? index} className="relative pb-4 last:pb-0">
            <span className="absolute -left-[19px] top-1 h-2 w-2 rounded-full bg-primary" />
            <p className="text-sm font-medium text-foreground">{item.summary}</p>
            {description && <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-foreground">{description}</p>}
            <p className="mt-1 text-xs text-muted-foreground">
              {formatDateTime(item.ts)}{item.actor?.name ? ` · ${item.actor.name}` : ''}
            </p>
          </li>
        );
      })}
    </ol>
  );
}

export function NotesPanel({
  entityType,
  entityId,
  entityLabel,
  activity,
}: {
  entityType: string;
  entityId: string;
  entityLabel: string;
  activity: ActivityItem[];
}) {
  const notes = activity.filter(
    (item) => item.action === 'record.note_added' || item.action.includes('record_interaction') && item.metadata?.interactionType === 'internal_note',
  );
  return (
    <DetailSection title="Notes">
      <div className="mb-3 flex items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">Internal context and handoff notes for this record.</p>
        <AddNoteDialog entityType={entityType} entityId={entityId} entityLabel={entityLabel} />
      </div>
      <ActivityTimeline items={notes} empty="No notes have been added yet." />
    </DetailSection>
  );
}

/**
 * The standardized simple-note panel for Contacts and Organizations.
 *
 * Same component calls and form entries use, so all four record types share one
 * button label, one composer, one saved-note presentation, one ordering, and
 * one empty state. The only difference is where the notes live: these two read
 * and write activity_logs, which is adapted to SharedRecordNote below.
 *
 * Leads keep NotesPanel above — their Add Note also records an interaction,
 * status, next step, and follow-up, so it is a different workflow and is being
 * handled separately.
 */
export function SimpleNotesPanel({
  entityType,
  entityId,
  entityLabel,
  activity,
  description,
}: {
  entityType: 'contact' | 'organization';
  entityId: string;
  entityLabel: string;
  activity: ActivityItem[];
  description?: string;
}) {
  const queryClient = useQueryClient();
  const notes = activity.filter(isSimpleNote).map(toSharedNote);

  return (
    <DetailSection title="Notes">
      <RecordNotes
        notes={notes}
        heading={null}
        description={description ?? `Internal context and handoff notes for ${entityLabel}.`}
        onAddNote={async (body) => {
          await leadloopApi.addNote({ entityType, entityId, entityLabel, note: body });
          await queryClient.invalidateQueries({ queryKey: ['activity', entityId] });
        }}
      />
    </DetailSection>
  );
}

function isSimpleNote(item: ActivityItem): boolean {
  if (item.action === 'record.note_added') return true;
  return (
    item.action.includes('record_interaction') &&
    item.metadata?.interactionType === 'internal_note'
  );
}

/**
 * activity_logs → SharedRecordNote. The note body lives in metadata under one
 * of two keys depending on which writer produced the row; the summary is the
 * fallback so a row never renders as an empty note.
 */
function toSharedNote(item: ActivityItem, index: number): SharedRecordNote {
  const body =
    (typeof item.metadata?.description === 'string' && item.metadata.description) ||
    (typeof item.metadata?.notes === 'string' && item.metadata.notes) ||
    item.summary;
  return {
    id: item.id ?? `${item.ts ?? 'note'}:${index}`,
    body,
    authorName: item.actor?.name ?? item.actor?.email ?? 'Cascade Admin',
    createdAt: item.ts ?? new Date(0).toISOString(),
  } as SharedRecordNote;
}

function AddNoteDialog({ entityType, entityId, entityLabel }: { entityType: string; entityId: string; entityLabel: string }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => leadloopApi.addNote({ entityType, entityId, entityLabel, note }),
    onSuccess: async () => {
      setNote('');
      setOpen(false);
      await queryClient.invalidateQueries({ queryKey: ['activity', entityId] });
    },
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="gap-1.5"><NotebookPen className="h-3.5 w-3.5" />Add note</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add note</DialogTitle>
          <DialogDescription>Add an internal note to {entityLabel}.</DialogDescription>
        </DialogHeader>
        <textarea
          value={note}
          onChange={(event) => setNote(event.target.value)}
          rows={6}
          maxLength={10000}
          autoFocus
          className="w-full resize-y rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground outline-none focus:ring-2 focus:ring-primary/30"
          placeholder="Add context, decisions, or follow-up details…"
        />
        {mutation.isError && <p className="text-sm text-destructive">Unable to save this note. Please try again.</p>}
        <DialogFooter>
          <Button variant="secondary" onClick={() => setOpen(false)} disabled={mutation.isPending}>Cancel</Button>
          <Button onClick={() => mutation.mutate()} disabled={!note.trim() || mutation.isPending}>
            {mutation.isPending ? 'Saving…' : 'Save note'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DetailTabBody({ children }: { children: ReactNode }) {
  return <div className="max-h-[calc(100vh-390px)] overflow-y-auto">{children}</div>;
}

export function LinkContactDialog({ leadId, currentContactId }: { leadId: string; currentContactId?: string }) {
  const [open, setOpen] = useState(false);
  const [contactId, setContactId] = useState(currentContactId ?? '');
  const queryClient = useQueryClient();
  const contactsQuery = useQuery({
    queryKey: ['contacts', ''],
    queryFn: () => leadloopApi.contacts(),
    enabled: open,
  });
  const mutation = useMutation({
    mutationFn: () => leadloopApi.linkContactToLead(contactId, leadId),
    onSuccess: async () => {
      setOpen(false);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['lead', leadId] }),
        queryClient.invalidateQueries({ queryKey: ['contacts'] }),
        queryClient.invalidateQueries({ queryKey: ['activity', leadId] }),
      ]);
    },
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline">{currentContactId ? 'Change contact' : 'Link contact'}</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Link a contact</DialogTitle>
          <DialogDescription>Select the contact that belongs to this lead. Existing form submissions and calls will appear automatically.</DialogDescription>
        </DialogHeader>
        <select
          value={contactId}
          onChange={(event) => setContactId(event.target.value)}
          className="h-10 w-full rounded-md border border-border bg-card px-3 text-sm text-foreground"
        >
          <option value="">Select a contact…</option>
          {(contactsQuery.data?.items ?? []).map((contact) => (
            <option key={contact.id} value={contact.id}>{contact.fullName}{contact.email ? ` — ${contact.email}` : ''}</option>
          ))}
        </select>
        {mutation.isError && <p className="text-sm text-destructive">Unable to link this contact. Please try again.</p>}
        <DialogFooter>
          <Button variant="secondary" onClick={() => setOpen(false)} disabled={mutation.isPending}>Cancel</Button>
          <Button onClick={() => mutation.mutate()} disabled={!contactId || mutation.isPending}>{mutation.isPending ? 'Linking…' : 'Link contact'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
